<script>
import { Icon, Button, Tag } from 'vant'

export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
    name: 'bottom-toolbar',

    //--------------------------------------------------
	// component components
	//--------------------------------------------------
    components: {
        'van-icon': Icon,
        'van-button': Button,
        'van-tag': Tag,
    },

    //--------------------------------------------------
    // component props
    //--------------------------------------------------
    props: {
        /**
         * The bottom toolbar type includes the following condition:
         * button - One button on the right side.
         * link-button - One label link on the left side and one button on the right side.
         * rate-button - One rate link (with count and total values) on the left side 
         * and one button on the right side.
         * button-button - One minor button on the left side and one main button on the right side.
         * link-icons - One label link on the left side and some icons on the right side.
         * rate-icons - One rate link on the left side and some icons on the right side.
         * button-icons - One minor button on the left side and some icons on the right side.
         * icons - Some icons on the right side.
         * tag-labels - One minor block on the left side and some labels 
         * (with the icon and text) on the right side.
         */
        type: String,
        mainButtonLabel: String,
        minorButtonLabel: String,
        minorLinkLabel: String,
        minorRateCountLabel: String,
        minorRateTotalLabel: String,
        minorRateLink: Boolean,
        minorTag: Object,
        mainLabels: Array,
        mainIcons: Array,
        mainButtonDisabled: Boolean,
        minorButtonDisabled: Boolean,
	},

    //--------------------------------------------------
    // component emits
    //--------------------------------------------------
	emits: [
        'click-minor-link', 
        'click-minor-rate', 
        'click-minor-button', 
        'click-minor-tag', 
        'click-main-button', 
        'click-main-icon',
        'click-main-label',
    ],

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        isMinorLink() {
            if (this.type && this.type.indexOf('link') == 0) {
                return true
            }
            return false
        },
        isMinorRate() {
            if (this.type && this.type.indexOf('rate') == 0) {
                return true
            }
            return false
        },
        isMinorButton() {
            if (this.type && this.type.indexOf('button-') == 0) {
                return true
            }
            return false
        },
        isMinorTag() {
            if (this.type && this.type.indexOf('tag-') == 0) {
                return true
            }
            return false
        },
        isMainButton() {
            if (this.type) {
                if (this.type == 'button' || this.type.indexOf('-button') > -1) {
                    return true
                }
            }
            return false
        },
        isMainIcons() {
            if (this.type && this.type.indexOf('icons') > -1) {
                return true
            }
            return false
        },
        isMainLabels() {
            if (this.type && this.type.indexOf('labels') > -1) {
                return true
            }
            return false
        },
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        onMainBtnClick() {
            this.$emit('click-main-button')
        },
        onMinorLinkClick() {
            this.$emit('click-minor-link')
        },
        onMinorRateClick() {
            this.$emit('click-minor-rate')
        },
        onMinorBtnClick() {
            this.$emit('click-minor-button')
        },
        onMinorTagClick() {
            this.$emit('click-minor-tag')
        },
        onMainIconClick(type) {
            this.$emit('click-main-icon', type)
        },
        onMainLabelClick(type) {
            this.$emit('click-main-label', type)
        },
        iconClass(icon) {
            const iconObject = {}
            iconObject['iconfont-' + icon] = true
            return iconObject
        },
    },
}
</script>

<template>
    <!-- component -->
    <div class="bottom-toolbar">
        <div class="bottom-height"></div>
        <div class="toolbar wp100 fixed-bottom bottom-bar">
            <div class="bar h50 vertical-center">
                <!-- minor section -->
                <div class="minor inline-block">
                    <div v-if="isMinorLink" 
                        class="minor-link"
                        @click="onMinorLinkClick"
                    >
                        <span class="main-text">{{ minorLinkLabel }}</span>
                        <span class="l-inner-margin primary-color"><van-icon name="arrow"/></span>
                    </div>
                    <div v-else-if="isMinorButton"
                        class="minor-button text-button"
                        :class="{ disabled: minorButtonDisabled }"
                        @click="onMinorBtnClick"
                    >
                        <span class="main-text">{{ minorButtonLabel }}</span>
                    </div>
                    <div v-else-if="isMinorRate"
                        class="vertical-center"
                        @click="onMinorRateClick"
                    >
                        <div class="inline-block normal-text">
                            <div class="text-right">
                                <span>{{ minorRateCountLabel }}</span>
                            </div>
                            <div class="text-right">
                                <span>{{ minorRateTotalLabel }}</span>
                            </div>
                        </div>
                        <div v-if="minorRateLink" class="inline-block">
                            <span class="l-inner-margin primary-color"><van-icon name="arrow"/></span>
                        </div>
                    </div>
                    <div v-else-if="isMinorTag" 
                        class="minor-tag" 
                        @click="onMinorTagClick"
                    >
                        <van-tag 
                            :color="minorTag.color" 
                            :text-color="minorTag.textColor"
                            size="large"
                        >
                            <template #default>
                                <span> {{ minorTag.label }}</span>
                                <van-icon v-if="minorTag.isLink" class="l-inner-margin" name="arrow"/>
                            </template>
                        </van-tag>
                    </div>
                </div>
                <!-- main section -->
                <div class="main inline-block">
                    <div v-if="isMainButton">
                        <van-button 
                            class="btn main-btn"
                            :class="{ 'van-button--disabled': mainButtonDisabled }"
                            round
                            type="info"
                            @click="onMainBtnClick"
                        >
                            <span class="main-text color-white">{{ mainButtonLabel }}</span>
                        </van-button>
                    </div>
                    <div v-else-if="isMainIcons" class="vertical-center">
                        <div class="icon-container">
                            <div 
                                class="icon-item inline-block text-center l-wide-margin"
                                v-for="item in mainIcons" 
                                :key="item.type" 
                                @click="onMainIconClick(item.type)"
                            >
                                <div>
                                    <van-icon 
                                        v-if="item.iconType == 'vant'" 
                                        class="primary-color icon"
                                        :name="item.icon"/>
                                    <span 
                                        v-else
                                        :class="iconClass(item.icon)" 
                                        class="iconfont primary-color icon"
                                    />
                                </div>
                                <div>
                                    <span class="minor-text main-secondary-color">{{ item.label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="isMainLabels" class="vertical-center">
                        <div class="label-container">
                            <div class="label-item inline-block text-center l-wide-margin"
                                v-for="item in mainLabels"
                                :key="item.type"
                                @click="onMainLabelClick(item.type)"
                            >
                                <div class="vertical-center">
                                <van-icon 
                                    v-if="item.iconType == 'vant'" 
                                    class="icon"
                                    :name="item.icon"
                                />
                                <span 
                                    v-else
                                    :class="iconClass(item.icon)" 
                                    class="iconfont icon"
                                />
                                <span class="normal-text main-secondary-color l-inner-margin">{{ item.label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="unsafe-area-bottom-mask"/>
    </div>
</template>

<style scoped>
.bar {
    padding: 0 22px;
}
.main-btn {
    position: absolute;
    right: 22px;
    top: 4px;
}
.icon-container,
.label-container {
    position: absolute;
    right: 22px;
}
.icon-item {
    width: 50px;
}
.main .icon-container .icon {
    font-size: 24px;
}
.main .label-container .icon {
    font-size: 21px;
}
</style>